import { useUpdateEffect } from '@cian/react-utils';

import { useEffect, useRef } from 'react';
import { batch, useDispatch } from 'react-redux';

import { useApplicationContext } from '@/app/shared/context/ApplicationContext';
import { type ListingSortType } from '@/app/shared/types/ListingSortType';
import { type IFiltersUpdatedEventDetail } from '@/app/shared/types/desktopFiltersMicrofrontendApi';
import { redirectToLocation } from '@/app/shared/utils/redirectToLocation';

import { FDealType, type TJsonQuery, getDealTypeFromJsonQuery } from '@/packages/JsonQuery';
import { useSearchOffersDesktopQuery } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/query';
import { type TReduxDispatch, boundActionsFactory } from '@/packages/redux-state/store';
import { useListingSortSelector, usePageSelector } from '@/packages/redux-state/store/slices/jsonQuery';
import { useActivePinIdSelector } from '@/packages/redux-state/store/slices/mapControls';
import { useMapSelectedBoundsSelector } from '@/packages/redux-state/store/slices/mapControls/selectors/dumb';
import { scrollToTopWithCallback } from '@/packages/utils';

const useBoundActions = boundActionsFactory(reduxActions => ({
  clearActivePin: reduxActions.mapControls.clearActivePin,
  clearSelectedBounds: reduxActions.mapControls.clearSelectedBounds,
  setFilters: reduxActions.jsonQuery.setFilters,
  setIsCurrentAreaSearchActive: reduxActions.mapControls.setIsCurrentAreaSearchActive,
  setListingPage: reduxActions.jsonQuery.setPageWithUpdateQueryKey,
  setListingSort: reduxActions.jsonQuery.setSort,
  setIsPageReloading: reduxActions.browserPage.setIsPageReloading,
}));

/**
 * Подписывается на событие из микрофронта на применение фильтров
 */
export const useUpdateOnFiltersUpdate = (): void => {
  const dispatch = useDispatch<TReduxDispatch>();
  const sort = useListingSortSelector();
  const page = usePageSelector();
  const activePinId = useActivePinIdSelector();
  const selectedBounds = useMapSelectedBoundsSelector();
  const ctx = useApplicationContext();

  const prevFiltersRef = useRef<TJsonQuery | null>(null);
  const prevPageRef = useRef<number | null>(null);
  const prevSortRef = useRef<ListingSortType | null>(null);

  const boundActions = useBoundActions();

  const { error } = useSearchOffersDesktopQuery({ notifyOnChangeProps: ['error'] });

  useUpdateEffect(() => {
    if (error) {
      /**
       * @description Откатываем обратно изменения в выдаче, которые мы сделали не дожидаясь сервера
       */
      batch(() => {
        if (prevFiltersRef.current) {
          boundActions.setFilters(prevFiltersRef.current);
        }
        if (prevSortRef.current) {
          boundActions.setListingSort(prevSortRef.current);
        }
        if (prevPageRef.current) {
          boundActions.setListingPage(prevPageRef.current);
        }
      });
    }
  }, [error]);

  useEffect(() => {
    const onFiltersUpdated = ({
      detail: { searchMeta: meta, jsonQuery, location },
    }: CustomEvent<IFiltersUpdatedEventDetail>): void => {
      const hasSearchMeta = 'fullUrl' in meta;

      /**
       * @description
       * fullUrl может отсутствовать только если мету в фильтрах еще не обновляли.
       * В таком случае придет пустой объект (в типах отражено).
       * Такое может быть только при первом рендере.
       * В таком случае нам не нужно ничего делать, только создать иммитацию, что фильтры применяются.
       */
      if (!hasSearchMeta) {
        boundActions.setIsPageReloading(true);

        setTimeout(() => boundActions.setIsPageReloading(false), 100);

        return;
      }

      const filters = jsonQuery;
      const newUrl = new URL(meta.fullUrl);

      const dealType = getDealTypeFromJsonQuery(filters);
      const excludedRegions = ctx.config.get<number[]>('BX.HybridSearch.ExcludedRegions');

      const isRegionAllowed =
        excludedRegions && excludedRegions.length > 0
          ? !excludedRegions.includes(location.parentId || location.id)
          : true;

      const shouldRedirectOnGeoChange =
        window.location.origin !== newUrl.origin && process.env.NODE_ENV !== 'development';

      const shouldRedirectOutOfExperiment = dealType !== FDealType.RentLongterm || !isRegionAllowed;

      // Делаем редирект при смене гео или при выходе из условий эксперимента
      const shouldRedirect = shouldRedirectOnGeoChange || shouldRedirectOutOfExperiment;

      if (shouldRedirect) {
        boundActions.setIsPageReloading(true);

        redirectToLocation(newUrl.href);

        return;
      }

      /**
       * @description Запоминаем прошлые значения сортировки, страницы пагинации и фильтров, нужно для оптимистик
       *   апдейта, в случае ошибки откатим обратно вот эти запомненные значения
       */
      {
        prevSortRef.current = sort;
        prevFiltersRef.current = filters;
        prevPageRef.current = page;
      }

      /**
       * @description Обновляем сразу всё, что можем обновить, не дожидаемся сервера, в случае ошибки откатим
       *   изменения, мы можем это сделать, так как выше их запомнили
       */
      batch(() => {
        /**
         * @description При обновлении фильтров очищаем выбранный пин на карте
         */
        if (activePinId) {
          boundActions.clearActivePin();
        }

        boundActions.setFilters(filters);

        scrollToTopWithCallback(() => {
          boundActions.setListingPage(1);
        });
      });
    };

    addEventListener('filters:updated', onFiltersUpdated);

    return (): void => {
      removeEventListener('filters:updated', onFiltersUpdated);
    };
  }, [dispatch, sort, page, activePinId, selectedBounds, ctx, boundActions]);
};
